import { useEffect, useState } from 'react';
import { createContext } from 'react';
import { useMutation } from 'react-query';
import { createCookie, loadCookie } from '../../api/query/cookie';
import CookieConsent from '../components/cookieConsent/CookieConsent';
import { COOKIE_STATUS, COOKIE_STATUS_DETAILS } from '../constants/cookie';

export const CookieContext = createContext();

const CookieContextProvider = ({ children }) => {
	const [manageCookieConsent, setManageCookieConsent] = useState(false);
	const [allowCookie, setAllowCookie] = useState({
		status: COOKIE_STATUS.LOADING,
	});
	const [cookieDetails, setCookieDetails] = useState([]);

	const setupCookieConsentByStatus = (status, details = []) => {
		if (status === COOKIE_STATUS.ALLOW && details === undefined) {
			details = [
				COOKIE_STATUS_DETAILS.ANALYTICAL,
				COOKIE_STATUS_DETAILS.FUNCTIONAL,
				COOKIE_STATUS_DETAILS.MARKETING,
				COOKIE_STATUS_DETAILS.PERSONALIZATION,
			];
		}

		if (status === COOKIE_STATUS.ALLOW) {
			const consentPayload = {
				ad_storage: 'denied',
				analytics_storage: 'denied',
				functionality_storage: 'denied',
				personalization_storage: 'granted',
			};

			if (details?.includes(COOKIE_STATUS_DETAILS.MARKETING)) {
				consentPayload.ad_storage = 'granted';
			}

			if (details?.includes(COOKIE_STATUS_DETAILS.ANALYTICAL)) {
				consentPayload.analytics_storage = 'granted';
			}

			if (details?.includes(COOKIE_STATUS_DETAILS.FUNCTIONAL)) {
				consentPayload.functionality_storage = 'granted';
			}

			if (details?.includes(COOKIE_STATUS_DETAILS.PERSONALIZATION)) {
				consentPayload.personalization_storage = 'granted';
			}

			window.uetq.push('consent', 'update', consentPayload);
			window?.uetq?.push('event', 'page view');
			window.gtag('consent', 'update', consentPayload);
		}

		if (status === COOKIE_STATUS.DENAIED) {
			const consentPayload = {
				ad_storage: 'denied',
				analytics_storage: 'denied',
				functionality_storage: 'denied',
				personalization_storage: 'denied',
			};

			window.uetq.push('consent', 'update', consentPayload);
			window.gtag('consent', 'update', consentPayload);
		}

		if (
			[
				COOKIE_STATUS.LOADING,
				COOKIE_STATUS.NO_DECISION,
				undefined,
				null,
			].includes(status)
		) {
			return setCookieDetails(() => [
				COOKIE_STATUS_DETAILS.ANALYTICAL,
				COOKIE_STATUS_DETAILS.FUNCTIONAL,
				COOKIE_STATUS_DETAILS.MARKETING,
				COOKIE_STATUS_DETAILS.PERSONALIZATION,
			]);
		}

		setCookieDetails(() => details);
	};

	const handleSetCookieMutation = useMutation({
		mutationFn: (data) => createCookie(data),
		onSuccess: ({ data }) => {
			setupCookieConsentByStatus(data?.status, data?.details);
			setAllowCookie(data);
		},
	});

	const handleLoadCookieMutation = useMutation({
		mutationFn: (data) => loadCookie(data),
		onSuccess: ({ data }) => {
			setupCookieConsentByStatus(data?.status, data?.details);
			setAllowCookie(data || { status: COOKIE_STATUS.NO_DECISION });
		},
	});

	const handleCookieDecision = (status, details) => {
		const payload = {
			status,
			details,
			consent_uid: localStorage.getItem('cookie_consent_uid'),
			uid: localStorage.getItem('uid'),
		};

		setAllowCookie(() => ({ status }));

		handleSetCookieMutation.mutate(payload);
	};

	const manageCookie = () => {
		setAllowCookie(() => ({ status: COOKIE_STATUS.NO_DECISION }));
		setManageCookieConsent(() => true);
	};

	const generateUid = () => {
		let uid =
			new Date().getTime().toString(16) + Math.floor(Math.random() * 1000);

		localStorage.setItem('cookie_consent_uid', uid);
		return uid;
	};

	useEffect(() => {
		let cookie = localStorage.getItem('cookie_consent_uid');

		if (!cookie) {
			cookie = generateUid();
		}

		const payload = {
			consent_uid: cookie,
		};

		handleLoadCookieMutation.mutate(payload);
	}, []);

	return (
		<CookieContext.Provider
			value={{
				handleCookieDecision,
				cookieDetails,
				setCookieDetails,
				manageCookie,
				manageCookieConsent,
				setManageCookieConsent,
			}}
		>
			{children}
			{allowCookie?.status === COOKIE_STATUS.NO_DECISION && <CookieConsent />}
		</CookieContext.Provider>
	);
};

export default CookieContextProvider;
